import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, Users, Loader2, ShieldCheck, Coffee, Waves, Sun, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useBooking } from '../../context/BookingContext';
import { useLanguage } from '../../context/LanguageContext';
import BookingModal from '../BookingModal';
import { trackBookingClick } from '../../hooks/useAnalytics';

const API = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

// Sticky "price & availability" card on a room page: dates → live Beds24 quote for this room → embedded booking engine pre-selected on the room.
export default function RoomPriceCard({ room, labels }) {
  const { t } = useLanguage();
  const { bookingDates, updateBookingDates } = useBooking();
  const [checkIn, setCheckIn] = useState(bookingDates.checkIn || '');
  const [checkOut, setCheckOut] = useState(bookingDates.checkOut || '');
  const [guests, setGuests] = useState(Math.min(bookingDates.guests || 2, room.guests || 2));
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const nights = checkIn && checkOut ? Math.max(0, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000)) : 0;

  useEffect(() => {
    if (!nights) { setQuote(null); return undefined; }
    let alive = true;
    setLoading(true);
    axios.get(`${API}/api/beds24/search-availability`, { params: { check_in: checkIn, check_out: checkOut, adults: guests, children: 0 } })
      .then((r) => {
        if (!alive) return;
        const live = (r.data?.rooms || []).find((x) => x.room_id === room.beds24Id);
        setQuote(live ? { live: true, available: live.available && live.total_price > 0, total: live.total_price, perNight: live.price_per_night } : { live: false, available: true });
      })
      .catch(() => alive && setQuote({ live: false, available: true }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [checkIn, checkOut, guests, nights, room.beds24Id]);

  const setDates = (ci, co) => {
    const co2 = ci && co && co <= ci ? plusDays(ci, 1) : co;
    setCheckIn(ci); setCheckOut(co2);
    if (ci && co2) updateBookingDates(ci, co2, guests);
  };
  const book = () => { trackBookingClick(); setOpen(true); };
  const nightWord = nights === 1 ? t('hero.night') : t('hero.nights');

  return (
    <aside className="lg:sticky lg:top-28 bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-900/5 p-6" data-testid="room-price-card">
      <p className="text-[11px] uppercase tracking-[0.25em] text-amber-700">{labels.priceTitle}</p>
      <div className="mt-2 flex items-end gap-2">
        <span className="text-sm text-slate-500 mb-1.5">{labels.from}</span>
        <span className="text-4xl font-bold text-slate-900" data-testid="room-base-price">€{Math.round(room.price)}</span>
        <span className="text-sm text-slate-500 mb-1.5">{labels.perNight}</span>
      </div>
      <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
        <li className="flex items-center gap-2"><Coffee className="w-4 h-4 text-amber-600" />{labels.breakfast}</li>
        <li className="flex items-center gap-2"><Sun className="w-4 h-4 text-amber-600" />{labels.balcony}</li>
        <li className="flex items-center gap-2"><Waves className="w-4 h-4 text-amber-600" />{labels.pools}</li>
      </ul>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <label className="text-xs text-slate-500">
          <span className="flex items-center gap-1 mb-1"><Calendar className="w-3.5 h-3.5" />{labels.checkIn}</span>
          <input type="date" value={checkIn} min={today()} onChange={(e) => setDates(e.target.value, checkOut)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500" data-testid="room-checkin" />
        </label>
        <label className="text-xs text-slate-500">
          <span className="flex items-center gap-1 mb-1"><Calendar className="w-3.5 h-3.5" />{labels.checkOut}</span>
          <input type="date" value={checkOut} min={checkIn ? plusDays(checkIn, 1) : today()} onChange={(e) => setDates(checkIn, e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500" data-testid="room-checkout" />
        </label>
        <label className="text-xs text-slate-500 col-span-2">
          <span className="flex items-center gap-1 mb-1"><Users className="w-3.5 h-3.5" />{labels.guests}</span>
          <select value={guests} onChange={(e) => { const g = Number(e.target.value); setGuests(g); if (checkIn && checkOut) updateBookingDates(checkIn, checkOut, g); }} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" data-testid="room-guests">
            {Array.from({ length: room.guests || 2 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} {n === 1 ? t('hero.guest') : t('hero.guests')}</option>)}
          </select>
        </label>
      </div>

      {nights > 0 && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm" data-testid="room-live-quote">
          {loading ? (
            <p className="flex items-center gap-2 text-slate-600"><Loader2 className="w-4 h-4 animate-spin" />{t('rooms.livePrice')}…</p>
          ) : quote?.live && quote.available ? (
            <>
              <div className="flex justify-between text-slate-600"><span>€{Math.round(quote.perNight)} × {nights} {nightWord}</span><span>€{Math.round(quote.total)}</span></div>
              <div className="flex justify-between font-semibold text-slate-900 mt-2 pt-2 border-t border-amber-200"><span>{labels.liveTotal}</span><span className="text-amber-700 text-lg" data-testid="room-live-total">€{Math.round(quote.total)}</span></div>
              <p className="text-xs text-slate-500 mt-1">{t('rooms.taxesIncluded')}</p>
            </>
          ) : quote?.live ? (
            <p className="text-slate-600" data-testid="room-unavailable">{labels.notAvailable}</p>
          ) : (
            <p className="text-slate-600">{Math.round(room.price)} € × {nights} {nightWord} · {t('rooms.clickToSeePrice')}</p>
          )}
        </div>
      )}

      <Button onClick={book} disabled={quote?.live && !quote.available} className="mt-5 w-full bg-amber-700 hover:bg-amber-800 text-white rounded-xl py-3.5 text-base flex items-center justify-center gap-2 disabled:opacity-50" data-testid="room-book-btn">
        {labels.check}<ArrowRight className="w-4 h-4" />
      </Button>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="w-3.5 h-3.5 text-green-600" />{labels.bestPrice}</p>

      <BookingModal isOpen={open} onClose={() => setOpen(false)} initialDates={{ checkIn, checkOut, guests }} roomId={room.beds24Id} />
    </aside>
  );
}
